import { useState, useEffect } from 'react';
import {
  collection, query, where, addDoc, updateDoc,
  onSnapshot, doc, serverTimestamp, getFirestore,
} from 'firebase/firestore';
import { getApp } from 'firebase/app';

export default function ExternalSharePanel({ post, postCol, currentUser, onClose }) {
  const db = getFirestore(getApp());
  const [existingLinks, setExistingLinks] = useState([]);
  const [form, setForm] = useState({ contactName:'', contactOrg:'', expiryDays:30, maxViews:0, note:'' });
  const [creating, setCreating] = useState(false);
  const [created,  setCreated]  = useState(null);
  const [copied,   setCopied]   = useState(false);

  const canShare = ['admin', 'ops'].includes(currentUser?.role);

  useEffect(() => {
    const q = query(collection(db, 'share_links'), where('reportId', '==', post.id));
    return onSnapshot(q, snap => setExistingLinks(snap.docs.map(d => ({ id:d.id, ...d.data() }))), () => {});
  }, [post.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const copyUrl = url => {
    navigator.clipboard.writeText(url).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const revokeLink = async (linkDocId) => {
    await updateDoc(doc(db, 'share_links', linkDocId), {
      isActive:  false,
      revokedAt: serverTimestamp(),
      revokedBy: currentUser?.uid,
    });
  };

  const handleCreate = async () => {
    if (!form.contactName.trim()) return;
    setCreating(true);
    try {
      const linkId = Math.random().toString(36).substring(2,11) + Date.now().toString(36);
      const expiresAt = form.expiryDays > 0 ? new Date(Date.now() + form.expiryDays * 86400000) : null;

      await addDoc(collection(db, 'share_links'), {
        linkId,
        reportId:          post.id,
        reportTitle:       post.title,
        reportCollection:  postCol,
        contactName:       form.contactName.trim(),
        contactOrg:        form.contactOrg.trim() || null,
        createdByUid:      currentUser?.uid,
        createdByName:     currentUser?.name,
        createdAt:         serverTimestamp(),
        expiresAt,
        maxViews:          form.maxViews > 0 ? form.maxViews : null,
        viewCount:         0,
        isActive:          true,
        note:              form.note.trim() || null,
        restrictions:      { noDownload:true, noCopyText:true, noPrint:true, watermarkEnabled:true },
      });

      await updateDoc(doc(db, postCol, post.id), {
        externalShared:     true,
        sharedExternallyBy: currentUser?.name,
        sharedExternallyAt: serverTimestamp(),
        externalShareNote:  form.note.trim() || null,
      });

      setCreated({ url: window.location.origin + '/r/' + linkId, contactName: form.contactName.trim() });
    } catch (err) {
      console.error('[ExternalShare]', err);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:500, display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{ width:480, background:'var(--smc-panel)', border:'0.5px solid rgba(127,119,221,0.40)', borderRadius:14, overflow:'hidden', maxHeight:'85vh', display:'flex', flexDirection:'column' }}>

        {/* Header */}
        <div style={{ padding:'14px 18px', borderBottom:'0.5px solid var(--smc-bd)', background:'rgba(127,119,221,0.08)', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <div>
            <div style={{ fontSize:14, fontWeight:600, color:'var(--smc-tx)' }}>Share outside organisation</div>
            <div style={{ fontSize:9, fontFamily:"'Share Tech Mono',monospace", color:'#7F77DD', marginTop:3, letterSpacing:'0.06em' }}>
              ADMIN / OPS ONLY · WATERMARKED · TRACKED
            </div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--smc-tx3)', fontSize:16 }}>✕</button>
        </div>

        <div style={{ flex:1, overflowY:'auto', padding:'16px 18px' }}>
          {!canShare ? (
            <div style={{ textAlign:'center', color:'var(--smc-tx3)', fontSize:12, padding:'20px 0' }}>
              🔒 Only admin and ops can share reports outside the organisation.
              <br /><br />Contact your admin to share this externally.
            </div>
          ) : (
            <>
              {/* Existing links */}
              {existingLinks.length > 0 && (
                <div style={{ marginBottom:18 }}>
                  <div style={{ fontSize:9, fontFamily:"'Share Tech Mono',monospace", color:'var(--smc-tx3)', letterSpacing:'0.1em', marginBottom:8 }}>
                    EXISTING LINKS ({existingLinks.length})
                  </div>
                  {existingLinks.map(link => (
                    <div key={link.id} style={{ background:'var(--smc-el)', border:'0.5px solid var(--smc-bd)', borderRadius:8, padding:'10px 12px', marginBottom:6 }}>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:5 }}>
                        <div style={{ fontSize:12, fontWeight:500, color:'var(--smc-tx)' }}>
                          {link.contactName}
                          {link.contactOrg && <span style={{ color:'var(--smc-tx3)', fontWeight:400 }}> · {link.contactOrg}</span>}
                        </div>
                        <span style={{ fontSize:8, padding:'2px 6px', borderRadius:3, fontFamily:"'Share Tech Mono',monospace", background: link.isActive ? 'rgba(29,158,117,0.12)' : 'rgba(255,255,255,0.05)', color: link.isActive ? '#1D9E75' : 'var(--smc-tx3)' }}>
                          {link.isActive ? 'ACTIVE' : 'REVOKED'}
                        </span>
                      </div>
                      <div style={{ fontSize:10, color:'var(--smc-tx3)', fontFamily:"'Share Tech Mono',monospace", marginBottom:6 }}>
                        {link.viewCount || 0} views · {link.expiresAt ? 'expires ' + new Date(link.expiresAt.seconds*1000).toLocaleDateString('en-IN',{day:'numeric',month:'short'}) : 'no expiry'} · by {link.createdByName}
                      </div>
                      <div style={{ display:'flex', gap:6 }}>
                        <button onClick={() => copyUrl(window.location.origin + '/r/' + link.linkId)} style={{ fontSize:10, padding:'4px 10px', borderRadius:5, background:'var(--smc-ac-bg)', color:'var(--smc-ac)', border:'0.5px solid var(--smc-ac-border)', cursor:'pointer' }}>
                          Copy link
                        </button>
                        {link.isActive && (
                          <button onClick={() => revokeLink(link.id)} style={{ fontSize:10, padding:'4px 10px', borderRadius:5, background:'rgba(226,75,74,0.10)', color:'#E24B4A', border:'0.5px solid rgba(226,75,74,0.25)', cursor:'pointer' }}>
                            Revoke
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  <div style={{ height:'0.5px', background:'var(--smc-bd)', margin:'12px 0' }} />
                </div>
              )}

              {/* Success state */}
              {created ? (
                <div style={{ background:'rgba(29,158,117,0.10)', border:'0.5px solid rgba(29,158,117,0.30)', borderRadius:10, padding:14 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'#1D9E75', marginBottom:8 }}>
                    ✓ Link created for {created.contactName}
                  </div>
                  <div style={{ background:'var(--smc-el)', border:'0.5px solid var(--smc-bd)', borderRadius:7, padding:'9px 12px', fontSize:11, fontFamily:"'Share Tech Mono',monospace", color:'var(--smc-tx2)', wordBreak:'break-all', marginBottom:10 }}>
                    {created.url}
                  </div>
                  <div style={{ display:'flex', gap:7 }}>
                    <button onClick={() => copyUrl(created.url)} style={{ flex:1, padding:8, borderRadius:7, background: copied ? '#10b981' : '#1D9E75', color:'#fff', border:'none', fontSize:12, fontWeight:500, cursor:'pointer' }}>
                      {copied ? '✓ Copied!' : 'Copy link'}
                    </button>
                    <button onClick={() => setCreated(null)} style={{ padding:'8px 14px', borderRadius:7, background:'transparent', color:'var(--smc-tx2)', border:'0.5px solid var(--smc-bd)', fontSize:12, cursor:'pointer' }}>
                      Create another
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ fontSize:9, fontFamily:"'Share Tech Mono',monospace", color:'var(--smc-tx3)', letterSpacing:'0.1em', marginBottom:12 }}>
                    CREATE NEW EXTERNAL LINK
                  </div>

                  {[
                    { key:'contactName', label:'RECIPIENT NAME *', placeholder:'Dr. Prafulla Kumar Jain' },
                    { key:'contactOrg',  label:'ORGANISATION (optional)', placeholder:'IN-SPACe / IPRC / Investor' },
                    { key:'note',        label:'INTERNAL NOTE (not shown to recipient)', placeholder:'e.g. Shared for IN-SPACe demo review' },
                  ].map(f => (
                    <div key={f.key} style={{ marginBottom:10 }}>
                      <div style={{ fontSize:10, fontFamily:"'Share Tech Mono',monospace", color:'var(--smc-tx3)', letterSpacing:'0.08em', marginBottom:4 }}>{f.label}</div>
                      <input value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                        placeholder={f.placeholder}
                        style={{ width:'100%', background:'var(--smc-el)', border:'0.5px solid var(--smc-bd)', borderRadius:7, padding:'8px 12px', fontSize:12, color:'var(--smc-tx)', outline:'none', fontFamily:'Inter, sans-serif', boxSizing:'border-box' }}
                      />
                    </div>
                  ))}

                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
                    {[
                      { key:'expiryDays', label:'EXPIRES AFTER', options:[{v:7,l:'7 days'},{v:30,l:'30 days'},{v:90,l:'90 days'},{v:0,l:'Never'}] },
                      { key:'maxViews',   label:'MAX VIEWS',     options:[{v:0,l:'Unlimited'},{v:1,l:'1 view'},{v:5,l:'5 views'},{v:10,l:'10 views'}] },
                    ].map(f => (
                      <div key={f.key}>
                        <div style={{ fontSize:10, fontFamily:"'Share Tech Mono',monospace", color:'var(--smc-tx3)', letterSpacing:'0.08em', marginBottom:4 }}>{f.label}</div>
                        <select value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: parseInt(e.target.value) }))}
                          style={{ width:'100%', background:'var(--smc-el)', border:'0.5px solid var(--smc-bd)', borderRadius:7, padding:'8px 12px', fontSize:12, color:'var(--smc-tx)', outline:'none' }}>
                          {f.options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>

                  <div style={{ padding:'8px 12px', borderRadius:7, background:'rgba(127,119,221,0.08)', border:'0.5px solid rgba(127,119,221,0.20)', fontSize:10, color:'#7F77DD', marginBottom:14 }}>
                    🔒 Link includes watermark · no download · no copy · access tracked
                  </div>

                  <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                    <button onClick={onClose} style={{ padding:'8px 16px', borderRadius:7, background:'transparent', border:'0.5px solid var(--smc-bd)', color:'var(--smc-tx2)', fontSize:12, cursor:'pointer' }}>
                      Cancel
                    </button>
                    <button onClick={handleCreate} disabled={creating || !form.contactName.trim()} style={{ padding:'8px 18px', borderRadius:7, background:'#7F77DD', color:'#fff', border:'none', fontSize:12, fontWeight:600, cursor: creating || !form.contactName.trim() ? 'not-allowed' : 'pointer', opacity: creating || !form.contactName.trim() ? 0.6 : 1 }}>
                      {creating ? 'Creating...' : 'Create link →'}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
